<template>
    <div class="grid-header">
        <div class="grid-title">
            <h1>Workload Interactions by Date</h1>
        </div>
    </div>
    <div class="grid-content">
        <div style="width: 100%; flex: 1 1 auto;">
            <ag-grid-vue
                class="ag-theme-alpine grid"
                :column-defs="columnDefs"
                :default-col-def="defaultColDef"
                :row-data="rowData"
                :on-grid-ready="onGridReady"
                :dom-layout="domLayout"
                :pagination="true"
                :pagination-page-size="10"
                :no-rows-overlay-component="NoRowsOverlay"
                :no-rows-overlay-component-params="noRowsOverlayParams"
                :loading-overlay-component="SpinningLoader"
                :default-excel-export-params="defaultExcelExportParams"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, defineProps } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import {
    GridApi,
    GridReadyEvent,
    ColumnApi
} from 'ag-grid-community';

import { InteractionSummaryTypes, BaseModel } from '../../types';
import { useWindowSize } from '../../composables/useWindowSize';
import { WorkstreamInteractionIntervalData } from '../interactionSummary/getData';
import numberValue from '../../utils/numberValue';

import NoRowsOverlay from '../common/noRowsOverlay/NoRowsOverlay.vue';
import SpinningLoader from '../common/loader/SpinningLoader.vue';

interface Props {
  data: WorkstreamInteractionIntervalData;
  workloads: BaseModel[];
}

const props = defineProps<Props>();
const columnDefs = ref<InteractionSummaryTypes.ColumnDefsType[] | []>([]);
const defaultColDef = ref<InteractionSummaryTypes.DefaultColumnDefsType>({
    resizable: true,
    filter: true,
    flex: 1,
    sortable: true,
    minWidth: 100,
    cellStyle: { 'text-align:': 'left' }
});

const defaultExcelExportParams = ref<InteractionSummaryTypes.DefaultExcelExportParamsType>({
    fileName: 'workload_interactions_by_date_export',
    sheetName: 'workload_interactions_by_date'
});

const formatValue = (decimalNum: number | string) => {
    if (decimalNum === '' || decimalNum === 0 || !decimalNum) {
        return 0;
    }
    const number = Number(decimalNum);
    return numberValue(number);
};
const rowData = ref<InteractionSummaryTypes.WorkstreamInteractionByDateRow[] | null>(null);
const gridApi = ref<GridApi | null>(null);
const gridColumnApi = ref<ColumnApi | null>(null);
const domLayout = 'autoHeight';
let noRowsOverlayMessage = 'Please apply a filter';
const noRowsOverlayParams = { noRowsMessageFunc: () => noRowsOverlayMessage };

const autoSizeAll = (skipHeader: boolean) => {
    if (gridColumnApi?.value) {
        const allColumnIds: string[] = [];
        gridColumnApi.value.getAllColumns()?.forEach((column) => {
            if (column) allColumnIds.push(column.getId());
        });

        gridColumnApi.value.autoSizeColumns(allColumnIds, skipHeader);
    }
};
const handleResize = () => {
    autoSizeAll(false);
};
const onGridReady = async (evt: GridReadyEvent) => {
    gridApi.value = evt.api;
    gridColumnApi.value = evt.columnApi;
    gridApi.value?.showNoRowsOverlay();

    await viewData(props.data);

    autoSizeAll(false);
};

useWindowSize(handleResize);

const getWorkloadName = (key: string) => {
    let workloadName = 'N/A';
    const nKey = Number.parseInt(key + '');
    if (!Number.isNaN(nKey)) {
        const foundWorkload = props.workloads.find((workload) => workload.id === nKey);
        if (foundWorkload) {
            workloadName = foundWorkload.name;
        }
    }
    return workloadName;
};

const viewData = async (data: WorkstreamInteractionIntervalData) => {
    gridApi?.value?.showLoadingOverlay();

    const sumColumns: InteractionSummaryTypes.ColumnDefsType = {
        headerName: '',
        children: [{ headerName: 'Date', field: 'date' }]
    };
    const sumManager: {
    [key: string]: InteractionSummaryTypes.WorkstreamInteractionByDateRow;
  } = {};
    const allColumns: InteractionSummaryTypes.ColumnDefsType[] = [sumColumns];

    Object.entries(data)
    // N/A is last
        .sort((a, b) => Number.parseInt(b[0]) - Number.parseInt(a[0]))
        .forEach(([ key, value ], index) => {
            const workloadName = getWorkloadName(key);
            // key is tab header name
            value.forEach((group) => {
                // initialize this group sums
                if (!sumManager[group.date]) {
                    sumManager[group.date] = {
                        date: group.date,
                        sumInteraction: 0,
                        sumTotalHandleTime: 0,
                        sumAverageHandleTime: 0
                    };
                }
                sumManager[group.date].sumInteraction += group.interactions;
                sumManager[group.date].sumTotalHandleTime += group.totalHandleTime;
                sumManager[group.date].sumAverageHandleTime += group.averageHandleTime;
                // append key value
                sumManager[group.date][`interactions${index}`] = group.interactions;
                sumManager[group.date][`totalHandleTime${index}`] = group.totalHandleTime;
                sumManager[group.date][`averageHandleTime${index}`] = group.averageHandleTime;
            });
            // construct columns
            allColumns.push({
                headerName: workloadName, // value of tabGroupName
                children: [
                    {
                        headerName: 'Total Interactions', field: `interactions${index}`, valueFormatter: (params) => formatValue(params.value)
                    },
                    {
                        headerName: 'Total Handle Time', field: `totalHandleTime${index}`, valueFormatter: (params) => formatValue(params.value)
                    },
                    {
                        headerName: 'Avg Handle Time', field: `averageHandleTime${index}`, valueFormatter: (params) => formatValue(params.value)
                    }
                ]
            });
        });
    columnDefs.value = allColumns;
    noRowsOverlayMessage = 'No rows to show';
    rowData.value = Object.entries(sumManager).map(([ _key, value ]) => value);
    setTimeout(() => {
        gridApi.value?.hideOverlay();
    }, 5000);
};
</script>

<style scoped lang="scss" src="./style.scss"></style>
